// firmenverbindungen.ts — die Firma↔Firma-Verbindungsarten an EINER Stelle.
//
// Bis 09/2026 standen dieselben Werte an sechs Stellen im Frontend nebeneinander
// (RelationCreateDialog, relationsInline/types, contactToRelationItems,
// rollenEinordnung, haushalteCleanupService, OrganigrammPopup) und an sechs
// weiteren im Backend. Keine davon kannte „Joint Venture", obwohl der
// Katalogeintrag seit Migration 202609040001 existiert — eine solche Kante war
// im Verbund-Scope und im Organigramm unsichtbar und fiel in der Maske still
// auf „Beteiligung" zurück.
//
// Zwei Begriffe, die hier bewusst auseinandergehalten werden:
//
//  • GESPEICHERTER TYP — das, was in "ContactRelationType" steht und an über 30
//    Stellen als Stringliteral verglichen wird. Umbenennen ist ausgeschlossen.
//  • ROLLE — das, was der Dialog anbietet. „Mutterfirma" und „Gesellschafterin"
//    sind KEINE gespeicherten Typen, sondern die Gegenrichtungen von
//    „Tochterfirma" bzw. „Beteiligung"; canonicalFirmaConn übersetzt sie in die
//    kanonische Zeile.
//
// Gegenstück im Backend: apps/api/src/firmaVerbindungstypen.ts. Die beiden
// Seiten teilen keinen Code; weichen sie voneinander ab, sieht der Anwender eine
// Verbindung, die der Verbund nicht kennt.

export const TOCHTERFIRMA = "Tochterfirma";
export const MUTTERFIRMA = "Mutterfirma";
export const SCHWESTER = "Schwester";
/** Altlabel aus dem Bestand — nur anzeigbar, nicht wählbar. */
export const SCHWESTERFIRMA_ALT = "Schwesterfirma";
export const BETEILIGUNG = "Beteiligung";
/**
 * Die Gegenrichtung von „Beteiligung": Die ANDERE Firma hält Anteile an
 * dieser. Wie „Mutterfirma" eine Rolle und kein gespeicherter Typ —
 * gespeichert wird eine 'Beteiligung'-Zeile mit gedrehter Richtung.
 *
 * Der WERT bleibt aus Kompatibilität „Gesellschafterin" (Formularzustand,
 * Tests, Bestandskanten 'Gesellschafter' Firma↔Firma laden als diese Rolle).
 * Angezeigt wird das Wort seit 02.10.2026 nicht mehr (User-Entscheidung,
 * Plan 2026-10-01 Paket A4): Zwischen zwei Firmen gibt es nur noch
 * Mutter/Tochter und Beteiligung — welche, entscheidet der Anwender.
 */
export const GESELLSCHAFTERIN = "Gesellschafterin";
export const JOINT_VENTURE = "Joint Venture";

/**
 * Was als Firma↔Firma-Strukturkante in der Datenbank stehen darf.
 * Wortgleich zu FIRMA_STRUKTUR_TYPEN in apps/api/src/firmaVerbindungstypen.ts.
 */
export const FIRMA_STRUKTUR_TYPEN_GESPEICHERT: readonly string[] = [
  TOCHTERFIRMA,
  SCHWESTER,
  BETEILIGUNG,
  JOINT_VENTURE,
];

/** Dieselbe Liste als Menge — für die Prüfung „ist das eine Strukturkante?". */
export const FIRMA_STRUKTUR_TYPEN_SET: ReadonlySet<string> = new Set(
  FIRMA_STRUKTUR_TYPEN_GESPEICHERT,
);

/**
 * Alle Labels, unter denen eine Strukturkante in der Akte AUFTAUCHEN kann —
 * gespeichert oder aus der Gegenrichtung gebildet. „Schwesterfirma" ist ein
 * Altlabel des Bestands und bleibt bewusst drin, damit solche Zeilen dort
 * stehen bleiben, wo sie heute stehen.
 */
export const FIRMA_STRUKTUR_LABELS: ReadonlySet<string> = new Set([
  ...FIRMA_STRUKTUR_TYPEN_GESPEICHERT,
  MUTTERFIRMA,
  GESELLSCHAFTERIN,
  SCHWESTERFIRMA_ALT,
]);

/** Ein Eintrag der Rollen-Auswahl: gespeicherter Wert + Beschriftung. */
export type FirmaConnRolle = { value: string; label: string };

/**
 * Was der Dialog „Firmen-Verbindung" anbietet.
 *
 * `value` ist der Schlüssel und geht durch canonicalFirmaConn; nur `label` ist
 * frei änderbar. Die Beschriftungen sagen die Richtung mit — die Nutzer
 * arbeiten ohne Technikwissen, und „Beteiligung" allein lässt offen, wer wen
 * hält.
 *
 * Seit 02.10.2026 (Paket A4) ohne „Gesellschafterin": Hält eine Firma Anteile
 * an einer anderen, ist sie Mutterfirma oder hat eine Beteiligung. Welches von
 * beiden, wählt der Anwender — keine Automatik und kein Hinweis nach der Quote
 * (User 02.10.2026). Die Gegenrichtung der Beteiligung heißt „Beteiligung an
 * dieser Firma".
 */
export const FIRMA_CONN_ROLES: readonly FirmaConnRolle[] = [
  { value: TOCHTERFIRMA, label: "Tochterfirma (gehört zu dieser Firma)" },
  { value: MUTTERFIRMA, label: "Mutterfirma (diese Firma gehört dazu)" },
  { value: SCHWESTER, label: "Schwesterfirma" },
  { value: BETEILIGUNG, label: "Beteiligung (diese Firma hält Anteile)" },
  { value: GESELLSCHAFTERIN, label: "Beteiligung an dieser Firma (die andere Firma hält Anteile)" },
  { value: JOINT_VENTURE, label: "Joint Venture" },
];

/** Kurzbeschriftung einer Rolle ohne Klammerzusatz — für Listen und Organigramm. */
export const firmaConnKurz = (role: string): string =>
  (FIRMA_CONN_ROLES.find((r) => r.value === role)?.label ?? role).replace(/\s*\(.*\)$/, "");

/**
 * Rollen je Art der Verbindung (Paket A5): „Unternehmen im Verbund" bzw.
 * „Unternehmen außerhalb des Verbunds". Die erste Rolle ist die Vorbelegung.
 */
export const FIRMA_CONN_GRUPPEN = {
  verbund: [TOCHTERFIRMA, MUTTERFIRMA, SCHWESTER],
  aussen: [BETEILIGUNG, GESELLSCHAFTERIN, JOINT_VENTURE],
  /** Die andere Firma hält Anteile an dieser (Gesellschaftertabelle, Paket A6). */
  halter: [MUTTERFIRMA, GESELLSCHAFTERIN],
} as const satisfies Record<string, readonly string[]>;
export type FirmaConnGruppe = keyof typeof FIRMA_CONN_GRUPPEN;

/** Nur die Werte — für `includes`-Prüfungen und Vorbelegungen. */
export const FIRMA_CONN_ROLE_VALUES: readonly string[] = FIRMA_CONN_ROLES.map((r) => r.value);

/**
 * Symmetrische Typen: Die Richtung trägt keine Aussage. Ein Speichern aus der
 * Gegenrichtung darf für sie KEINE zweite Zeile anlegen.
 *
 * „Beteiligung" gehört seit 09/2026 NICHT mehr dazu: src ist die Gesellschaft,
 * dest der Gesellschafter (Entscheidung T2). Die Richtung ist die Aussage.
 */
export const FIRMA_SYMMETRIC: ReadonlySet<string> = new Set([
  SCHWESTER,
  SCHWESTERFIRMA_ALT,
  JOINT_VENTURE,
]);

/**
 * Typ-Inverse für die Anzeige einer EINGEHENDEN Zeile (der aktuelle Kontakt ist
 * contactDest). Geschlechts-unabhängig, anders als die Verwandtschafts-Inverse.
 * Fehlt ein Schlüssel, bleibt das Label unverändert (die Aufrufer nutzen `?? label`).
 *
 * „Beteiligung" steht bewusst NICHT mehr drin: Eine eingehende Beteiligung
 * bedeutet „dieser Kontakt hält Anteile an der anderen Firma" — und genau so
 * liest sich das Wort neben dem Namen der anderen Firma. Der Rückfall auf das
 * unveränderte Label ist hier die richtige Anzeige, nicht eine fehlende Regel.
 */
export const FIRMA_INVERSE: Readonly<Record<string, string>> = {
  [TOCHTERFIRMA]: MUTTERFIRMA,
  [MUTTERFIRMA]: TOCHTERFIRMA,
  [SCHWESTER]: SCHWESTER,
  [SCHWESTERFIRMA_ALT]: SCHWESTERFIRMA_ALT,
  [JOINT_VENTURE]: JOINT_VENTURE,
};

/**
 * Gespeicherter Typ + Richtung → ROLLE aus Sicht des geöffneten Kontakts.
 *
 * Das Gegenstück zu {@link canonicalFirmaConn}: Was der Anwender in
 * der Auswahl sehen muss, damit ein bloßes Öffnen-und-Speichern die Zeile nicht
 * umdreht. Zwei gerichtete Typen, beide kanonisch als EINE Zeile gehalten:
 *
 *   'Tochterfirma': src = Eltern,       dest = Kind          → auf der dest-Seite „Mutterfirma".
 *   'Beteiligung':  src = Gesellschaft, dest = Gesellschafter → auf der src-Seite „Gesellschafterin".
 *
 * `istSrc` = der geöffnete Kontakt steht auf der src-Seite der Zeile. Ein
 * unbekannter Typ (etwa das Altlabel „Schwesterfirma") kommt unverändert zurück
 * — ihn hier auf etwas anderes zu ziehen, hieße den Bestand beim Speichern
 * umzuschreiben.
 */
export function firmaConnRolleAusRichtung(gespeicherterTyp: string, istSrc: boolean): string {
  const typ = (gespeicherterTyp ?? "").trim();
  if (typ === TOCHTERFIRMA) return istSrc ? TOCHTERFIRMA : MUTTERFIRMA;
  if (typ === BETEILIGUNG) return istSrc ? GESELLSCHAFTERIN : BETEILIGUNG;
  return typ;
}

/**
 * Rolle → kanonische Zeile (geteilt von Detail-, Anlage- und Bereinigungs-Pfad).
 *
 * Zwei gepinnte Konventionen:
 *   Eltern/Kind:   src = ELTERN,       Typ 'Tochterfirma', dest = KIND.
 *   Beteiligung:   src = GESELLSCHAFT, Typ 'Beteiligung',  dest = GESELLSCHAFTER.
 *
 * Dieselbe Richtung schreibt `ensureGesellschafterAusMutter` im Backend und der
 * Register-Import. Es wird nie eine 'Mutterfirma'- und nie eine
 * 'Gesellschafterin'-Zeile gespeichert; beide sind nur die Gegenrichtung.
 *
 * C = aktueller Kontakt (`selfId`), F = gewählte Firma (`otherId`):
 *   - 'Tochterfirma'     (F ist Tochter von C):     src=C, dest=F, 'Tochterfirma'.
 *   - 'Mutterfirma'      (F ist Mutter von C):      src=F, dest=C, 'Tochterfirma' ← FLIP.
 *   - 'Beteiligung'      (C hält Anteile an F):     src=F, dest=C, 'Beteiligung'  ← FLIP.
 *   - 'Gesellschafterin' (F hält Anteile an C):     src=C, dest=F, 'Beteiligung'.
 *   - 'Schwester' / 'Joint Venture' (symmetrisch):  src=C, dest=F, Typ unverändert.
 */
export function canonicalFirmaConn(
  role: string,
  selfId: number,
  otherId: number,
): { contactSrcId: number; contactDestId: number; relationType: string } {
  if (role === MUTTERFIRMA) {
    return { contactSrcId: otherId, contactDestId: selfId, relationType: TOCHTERFIRMA };
  }
  if (role === BETEILIGUNG) {
    // C hält Anteile an F → F ist die Gesellschaft und steht in src.
    return { contactSrcId: otherId, contactDestId: selfId, relationType: BETEILIGUNG };
  }
  if (role === GESELLSCHAFTERIN) {
    // F hält Anteile an C → C ist die Gesellschaft.
    return { contactSrcId: selfId, contactDestId: otherId, relationType: BETEILIGUNG };
  }
  return { contactSrcId: selfId, contactDestId: otherId, relationType: role };
}

/**
 * Hält die ANDERE Firma Anteile an diesem Unternehmen (Rolle „Mutterfirma"
 * oder „Gesellschafterin")? Solche Einträge stehen in der Firmenkarte unter
 * „Unternehmensleitung und Gesellschafter", nicht bei den Verbindungen — die
 * zeigen nur, woran DIESES Unternehmen beteiligt ist (User 28.09.2026).
 */
export const istFremdeBeteiligung = (it: { role?: string | null }): boolean =>
  it.role === GESELLSCHAFTERIN || it.role === MUTTERFIRMA;
